import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Grid, Card, CardContent, CardHeader,
  Box, CircularProgress, Alert, ToggleButton, ToggleButtonGroup,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Divider, FormGroup, FormControlLabel, Checkbox, Switch,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Devices as DevicesIcon,
  FilterAlt as FunnelIcon,
  FamilyRestroom as FamilyIcon,
  ShowChart as ShowChartIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, LabelList,
  PieChart, Pie, Legend,
  LineChart, Line,
} from 'recharts';
import AdminLayout from '../../layouts/AdminLayout';
import AcquisitionSourcesSection from '../../components/admin/AcquisitionSourcesSection';
import { AdminService } from '../../services/adminService';

// ─── Palette cohérente ────────────────────────────────────────────────────────
const PLATFORM_COLORS = {
  ios:           '#0071E3',
  android:       '#3DDC84',
  web_app:       '#7C4DFF',
  web_marketing: '#FF6D00',
  parent_portal: '#00BFA5',
  unknown:       '#9E9E9E',
  other:         '#BDBDBD',
};
const FUNNEL_COLOR = '#5C6BC0';
const CTA_COLOR    = '#26A69A';

const PERIOD_OPTIONS = [
  { value: 7,  label: '7 j' },
  { value: 30, label: '30 j' },
  { value: 90, label: '90 j' },
];

// Étapes clés à afficher dans le graphe de tendance (sous-ensemble lisible)
/**
 * Jalons de l'entonnoir — des FAITS MÉTIER, plus des étapes d'écran. La collecte
 * `onboarding_step` du client n'a jamais émis une ligne (gate `acceptAnalytics`),
 * si bien que ce graphe était plat à zéro. Les clés doivent rester identiques à
 * `adminQueries.ONBOARDING_JALONS` côté backend.
 *
 * ⚠️ L'ordre est celui du tunnel en vigueur (`onboarding.config.ts`), où le chemin
 * de forêt précède le compagnon depuis le 2026-09-08.
 */
const TREND_STEPS = [
  { key: 'signup',          label: 'Inscriptions',       color: '#42A5F5' },
  { key: 'combat_1',        label: 'Chemin de forêt',    color: '#FFA726' },
  { key: 'companion',       label: 'Compagnon créé',     color: '#66BB6A' },
  { key: 'combat_2',        label: 'Combat monstre',     color: '#EF5350' },
  { key: 'onboarding_done', label: 'Onboarding terminé', color: '#26C6DA' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const pct = (part, total) =>
  total > 0 ? `${((part / total) * 100).toFixed(1)} %` : '—';

const fmtNum = (n) => (n ?? 0).toLocaleString('fr-FR');

// ─── Composants locaux ────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, color = 'primary' }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h3" fontWeight="bold" color={`${color}.main`}>
          {fmtNum(value)}
        </Typography>
        {sub && (
          <Typography variant="body2" color="text.secondary" mt={0.5}>{sub}</Typography>
        )}
      </CardContent>
    </Card>
  );
}

function SectionTitle({ icon, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, mt: 1 }}>
      {icon}
      <Typography variant="h6" fontWeight="bold">{children}</Typography>
    </Box>
  );
}

// ─── Tooltip personnalisé ─────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', p: 1.5, borderRadius: 1 }}>
      <Typography variant="body2" fontWeight="bold">{label}</Typography>
      {payload.map((p) => (
        <Typography key={p.dataKey} variant="body2" color={p.color}>
          {p.name} : <strong>{fmtNum(p.value)}</strong>
        </Typography>
      ))}
    </Box>
  );
}

// ─── Écran principal ──────────────────────────────────────────────────────────
export default function MarketingScreen() {
  const [days, setDays]             = useState(30);
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [trendMode, setTrendMode]   = useState('proportion'); // 'proportion' | 'absolute'
  const [activeSteps, setActiveSteps] = useState(
    Object.fromEntries(TREND_STEPS.map(s => [s.key, true]))
  );

  const toggleStep = (key) =>
    setActiveSteps(prev => ({ ...prev, [key]: !prev[key] }));

  const load = useCallback(async (d) => {
    setLoading(true);
    setError(null);
    try {
      const result = await AdminService.getMarketingAnalytics(d);
      setData(result);
    } catch (err) {
      setError('Impossible de charger les données analytics.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  // ── KPI dérivés ─────────────────────────────────────────────────────────────
  const totalSignups      = data?.funnel?.[0]?.users ?? 0;
  const totalVisitors     = Number(data?.totalUniqueVisitors ?? (data?.referrers?.reduce((s, r) => s + Number(r.uniqueVisitors ?? 0), 0) ?? 0));
  const returningVisitors = Number(data?.returningVisitors ?? 0);
  const returnRate        = totalVisitors > 0 ? ((returningVisitors / totalVisitors) * 100).toFixed(1) : '—';
  const totalCTAClicks    = data?.ctaClicks?.reduce((s, c) => s + Number(c.clicks ?? 0), 0) ?? 0;
  const finalizationUsers = data?.funnel?.find(f => f.step === 'onboarding_done')?.users ?? 0;
  const completionRate    = totalSignups > 0 ? ((finalizationUsers / totalSignups) * 100).toFixed(1) : '—';

  return (
    <AdminLayout>
      {/* ── En-tête ────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Marketing & Acquisition</Typography>
          <Typography variant="body2" color="text.secondary">
            Sources d'inscription, parcours utilisateur et distribution des plateformes
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={days}
          exclusive
          size="small"
          onChange={(_, v) => { if (v) setDays(v); }}
        >
          {PERIOD_OPTIONS.map(o => (
            <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      ) : data && (
        <>
          {/* ── KPIs ──────────────────────────────────────────────────────── */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} md={3}>
              <KpiCard label="Inscriptions" value={totalSignups} color="primary" sub={`sur ${days} jours`} />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard
                label="Visiteurs uniques (site)"
                value={totalVisitors}
                color="info"
                sub={`Retour: ${fmtNum(returningVisitors)} (${returnRate} %)`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard label="Clics CTA" value={totalCTAClicks} color="warning" />
            </Grid>
            <Grid item xs={6} md={3}>
              <KpiCard
                label="Complétion onboarding"
                value={`${completionRate} %`}
                color="success"
                sub={`${fmtNum(finalizationUsers)} / ${fmtNum(totalSignups)} inscrits`}
              />
            </Grid>
          </Grid>

          {/* ── Section 1 : Origines ──────────────────────────────────────── */}
          <Divider sx={{ mb: 3 }} />
          <SectionTitle icon={<TrendingUpIcon color="info" />}>
            Origines du trafic
          </SectionTitle>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Courbe visiteurs journaliers (hors bots, tous referrers) */}
            <Grid item xs={12}>
              <Card>
                <CardHeader
                  title="Visiteurs uniques journaliers (hors bots)"
                  subheader={`Tous referrers confondus sur ${days} jours`}
                />
                <CardContent>
                  {!data.dailyVisitors || data.dailyVisitors.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={data.dailyVisitors} margin={{ left: 10, right: 20, top: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Line
                          type="monotone"
                          dataKey="uniqueVisitors"
                          name="Visiteurs uniques"
                          stroke="#1E88E5"
                          strokeWidth={3}
                          dot={{ r: 2 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Referrers — bar horizontal */}
            <Grid item xs={12} md={7}>
              <Card>
                <CardHeader title="Top referrers (visites site)" subheader="par visiteurs uniques" />
                <CardContent>
                  {data.referrers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart
                        data={data.referrers}
                        layout="vertical"
                        margin={{ left: 10, right: 40, top: 0, bottom: 0 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis type="category" dataKey="referrer" width={140} tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="uniqueVisitors" name="Visiteurs uniques" fill="#42A5F5" radius={[0, 4, 4, 0]}>
                          <LabelList dataKey="uniqueVisitors" position="right" style={{ fontSize: 11 }} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* CTA Clicks — table */}
            <Grid item xs={12} md={5}>
              <Card sx={{ height: '100%' }}>
                <CardHeader title="Clics CTA" subheader="par bouton" />
                <CardContent sx={{ p: 0 }}>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>CTA</TableCell>
                          <TableCell align="right">Clics</TableCell>
                          <TableCell align="right">Prospects uniques</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.ctaClicks.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary">Aucun clic enregistré</Typography>
                            </TableCell>
                          </TableRow>
                        ) : data.ctaClicks.map((row) => (
                          <TableRow key={row.ctaName} hover>
                            <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row.ctaName}
                            </TableCell>
                            <TableCell align="right"><strong>{fmtNum(row.clicks)}</strong></TableCell>
                            <TableCell align="right">{fmtNum(row.uniqueProspects)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* ── Section 2 : Funnel d'inscription ─────────────────────────── */}
          <Divider sx={{ mb: 3 }} />
          <SectionTitle icon={<FunnelIcon color="secondary" />}>
            Funnel inscription &amp; onboarding
          </SectionTitle>

          <Card sx={{ mb: 4 }}>
            <CardContent>
              {/* ⚠️ Avant l'instrumentation, les jalons de combat sont vides PAR
                  CONSTRUCTION : `combats.contexte` n'étiquetait pas encore les
                  combats d'onboarding. Sans ce rappel, on lit un effondrement. */}
              {data.instrumentedSince && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Jalons dérivés de ce que l'enfant a réellement fait (compte créé, compagnon
                  créé, combats d'onboarding terminés, onboarding achevé) — rien n'est collecté
                  en plus. « Chemin de forêt » et « Combat monstre » ne sont mesurés que depuis
                  le <strong>{new Date(data.instrumentedSince).toLocaleDateString('fr-FR')}</strong>,
                  et un combat n'est compté qu'une fois <em>terminé</em> — le bouton « Passer »
                  ne laisse aucune trace.
                </Alert>
              )}
              {data.funnel.every(s => s.users === 0) ? (
                <Typography variant="body2" color="text.secondary">
                  Aucune donnée d'onboarding sur cette période.
                </Typography>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={data.funnel}
                      margin={{ top: 10, right: 30, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="users" name="Utilisateurs" fill={FUNNEL_COLOR} radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="users" position="top" style={{ fontSize: 12, fontWeight: 'bold' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>

                  {/* 🔴 Part des INSCRIPTIONS, jamais « de l'étape précédente » : ces
                      jalons ne forment pas une chaîne stricte (le bouton « Passer »
                      des combats laisse finir l'onboarding sans les avoir joués), et
                      le ratio d'étape à étape rendait 425 % en production. */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    {data.funnel.slice(1).map((step) => {
                      const part = step.survivalRate ?? 0;
                      return (
                        <Chip
                          key={step.step}
                          size="small"
                          label={`${step.label} : ${part} % des inscriptions`}
                          variant="outlined"
                          color={part >= 70 ? 'success' : part >= 40 ? 'warning' : 'error'}
                        />
                      );
                    })}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          {/* ── Section 3 : Conversion email parent ──────────────────────── */}
          {data.parentEmailConversion && (
            <>
              <Divider sx={{ mb: 3 }} />
              <SectionTitle icon={<FamilyIcon color="error" />}>
                Conversion email parent (enfants &lt; 15 ans)
              </SectionTitle>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {(() => {
                  const p = data.parentEmailConversion;
                  const total     = p.totalSent  ?? 0;
                  const validated = p.validated  ?? 0;
                  const refused   = p.refused    ?? 0;
                  const pending   = p.pending    ?? 0;
                  const expired   = p.expired    ?? 0;
                  const validRate = total > 0 ? ((validated / total) * 100).toFixed(1) : '—';
                  return (
                    <>
                      <Grid item xs={6} md={3}>
                        <KpiCard label="Emails envoyés" value={total} color="primary" sub={`sur ${days} jours`} />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <KpiCard label="Comptes parent créés" value={validated} color="success" sub={`${validRate} % de conversion`} />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <KpiCard label="Refusés" value={refused} color="error" sub={`${pct(refused, total)} du total`} />
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <KpiCard label="En attente / expirés" value={pending} color="warning" sub={`${fmtNum(expired)} expirés`} />
                      </Grid>
                      <Grid item xs={12}>
                        <Card>
                          <CardContent>
                            <ResponsiveContainer width="100%" height={120}>
                              <BarChart
                                layout="vertical"
                                data={[
                                  { label: 'Validés',  value: validated,         fill: '#66BB6A' },
                                  { label: 'Refusés',  value: refused,            fill: '#EF5350' },
                                  { label: 'En attente', value: pending,          fill: '#FFA726' },
                                  { label: 'Expirés',  value: expired,            fill: '#BDBDBD' },
                                ]}
                                margin={{ left: 80, right: 40, top: 0, bottom: 0 }}
                              >
                                <XAxis type="number" allowDecimals={false} />
                                <YAxis type="category" dataKey="label" width={70} tick={{ fontSize: 12 }} />
                                <Tooltip formatter={(v) => fmtNum(v)} />
                                <Bar dataKey="value" name="Comptes" radius={[0, 4, 4, 0]}>
                                  {[
                                    { label: 'Validés',   fill: '#66BB6A' },
                                    { label: 'Refusés',   fill: '#EF5350' },
                                    { label: 'En attente', fill: '#FFA726' },
                                    { label: 'Expirés',   fill: '#BDBDBD' },
                                  ].map((e) => <Cell key={e.label} fill={e.fill} />)}
                                  <LabelList dataKey="value" position="right" style={{ fontSize: 11 }} />
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </CardContent>
                        </Card>
                      </Grid>
                    </>
                  );
                })()}
              </Grid>
            </>
          )}

          {/* ── Section 4 : Tendance funnel par semaine ───────────────────── */}
          {data.funnelTrend && data.funnelTrend.length > 0 && (
            <>
              <Divider sx={{ mb: 3 }} />
              <SectionTitle icon={<ShowChartIcon color="primary" />}>
                Tendance par jalon (par semaine d'inscription)
              </SectionTitle>
              <Card sx={{ mb: 4 }}>
                <CardContent>
                  {/* Contrôles */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={trendMode === 'proportion'}
                          onChange={() => setTrendMode(m => m === 'proportion' ? 'absolute' : 'proportion')}
                        />
                      }
                      label={trendMode === 'proportion' ? 'Proportion (%)' : 'Absolu (users)'}
                    />
                    <FormGroup row>
                      {TREND_STEPS.map(s => (
                        <FormControlLabel
                          key={s.key}
                          control={
                            <Checkbox
                              size="small"
                              checked={activeSteps[s.key]}
                              onChange={() => toggleStep(s.key)}
                              sx={{ color: s.color, '&.Mui-checked': { color: s.color } }}
                            />
                          }
                          label={<Typography variant="caption">{s.label}</Typography>}
                        />
                      ))}
                    </FormGroup>
                  </Box>

                  {/* Graphe */}
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart
                      data={data.funnelTrend.map(week => {
                        const base = week[TREND_STEPS[0].key] || 1;
                        const row = { week: week.week };
                        TREND_STEPS.forEach(s => {
                          const raw = week[s.key] ?? 0;
                          row[s.key] = trendMode === 'proportion'
                            ? base > 0 ? parseFloat(((raw / base) * 100).toFixed(1)) : 0
                            : raw;
                        });
                        return row;
                      })}
                      margin={{ left: 10, right: 20, top: 8, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                      <YAxis
                        allowDecimals={trendMode === 'proportion'}
                        tickFormatter={v => trendMode === 'proportion' ? `${v} %` : fmtNum(v)}
                      />
                      <Tooltip
                        formatter={(v, name) => {
                          const step = TREND_STEPS.find(s => s.key === name);
                          return [trendMode === 'proportion' ? `${v} %` : fmtNum(v), step?.label ?? name];
                        }}
                      />
                      <Legend
                        formatter={(value) => {
                          const step = TREND_STEPS.find(s => s.key === value);
                          return step?.label ?? value;
                        }}
                      />
                      {TREND_STEPS.filter(s => activeSteps[s.key]).map(s => (
                        <Line
                          key={s.key}
                          type="monotone"
                          dataKey={s.key}
                          name={s.key}
                          stroke={s.color}
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                  {trendMode === 'proportion' && (
                    <Typography variant="caption" color="text.secondary">
                      Proportion calculée par rapport à « Création perso » de la même semaine.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* ── Section 6 : Plateformes & UTM ────────────────────────────── */}
          <Divider sx={{ mb: 3 }} />
          <SectionTitle icon={<DevicesIcon color="action" />}>
            Plateformes & acquisition par source
          </SectionTitle>

          <Grid container spacing={3}>
            {/* Pie plateformes */}
            <Grid item xs={12} md={5}>
              <Card>
                <CardHeader title="Plateforme d'inscription" />
                <CardContent>
                  {data.platforms.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Aucune donnée</Typography>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={data.platforms}
                          dataKey="count"
                          nameKey="platform"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={({ platform, percent }) =>
                            `${platform} (${(percent * 100).toFixed(0)} %)`
                          }
                        >
                          {data.platforms.map((entry) => (
                            <Cell
                              key={entry.platform}
                              fill={PLATFORM_COLORS[entry.platform] ?? '#90A4AE'}
                            />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v, n) => [fmtNum(v), n]} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Acquisition par source du compte (convention UTM v2) */}
            <Grid item xs={12}>
              <AcquisitionSourcesSection days={days} />
            </Grid>
          </Grid>
        </>
      )}
    </AdminLayout>
  );
}
